import React, { useState } from 'react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Création du contenu de l'email
      const emailBody = `Nouveau message depuis le site OZARKS ELEC

Nom: ${formData.name}
Email: ${formData.email}
Téléphone: ${formData.phone}
Sujet: ${formData.subject}

Message:
${formData.message}

---
Envoyé depuis le formulaire de contact du site ozarkselec.com`;

      // Utilisation de Gmail compose directement
      const gmailComposeUrl = `https://mail.google.com/mail/?view=cm&to=ozarkselec@gmail.com&su=${encodeURIComponent(`Contact site: ${formData.subject}`)}&body=${encodeURIComponent(emailBody)}`;
      
      // Ouvrir Gmail compose dans un nouvel onglet
      window.open(gmailComposeUrl, '_blank');
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      backgroundColor: 'var(--ozarks-white)',
      padding: '40px',
      borderRadius: '12px',
      boxShadow: '0 8px 25px var(--ozarks-shadow-medium)',
      marginTop: '40px'
    }}>
      <h3 className="ozarks-blue-dark" style={{marginBottom: '25px', fontWeight: 600, textAlign: 'center'}}>
        ✉️ Formulaire de contact
      </h3>
      
      <form onSubmit={handleSubmit}>
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
          <div>
            <label htmlFor="name" style={{display: 'block', marginBottom: '8px', fontWeight: 500, color: 'var(--ozarks-blue-dark)'}}>
              Nom complet *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #E9ECEF',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--ozarks-blue-light)'}
              onBlur={(e) => e.target.style.borderColor = '#E9ECEF'}
            />
          </div>
          
          <div>
            <label htmlFor="phone" style={{display: 'block', marginBottom: '8px', fontWeight: 500, color: 'var(--ozarks-blue-dark)'}}>
              Téléphone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #E9ECEF',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--ozarks-blue-light)'}
              onBlur={(e) => e.target.style.borderColor = '#E9ECEF'}
            />
          </div>
        </div>

        <div style={{marginBottom: '20px'}}>
          <label htmlFor="email" style={{display: 'block', marginBottom: '8px', fontWeight: 500, color: 'var(--ozarks-blue-dark)'}}>
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #E9ECEF',
              borderRadius: '8px',
              fontSize: '16px',
              transition: 'border-color 0.3s ease',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--ozarks-blue-light)'}
            onBlur={(e) => e.target.style.borderColor = '#E9ECEF'}
          />
        </div>

        <div style={{marginBottom: '20px'}}>
          <label htmlFor="subject" style={{display: 'block', marginBottom: '8px', fontWeight: 500, color: 'var(--ozarks-blue-dark)'}}>
            Sujet *
          </label>
          <select
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #E9ECEF',
              borderRadius: '8px',
              fontSize: '16px',
              transition: 'border-color 0.3s ease',
              backgroundColor: 'white',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--ozarks-blue-light)'}
            onBlur={(e) => e.target.style.borderColor = '#E9ECEF'}
          >
            <option value="">Sélectionnez un sujet</option>
            <option value="Devis électricité">Devis - Électricité générale</option>
            <option value="Devis bâtiment">Devis - Bâtiment général</option>
            <option value="Devis industriel">Devis - Électricité industrielle</option>
            <option value="Devis domotique">Devis - Domotique</option>
            <option value="Devis éclairage">Devis - Éclairage LED</option>
            <option value="Formation">Formation - Habilitation électrique</option>
            <option value="Urgence">Urgence électrique</option>
            <option value="Autre">Autre demande</option>
          </select>
        </div>

        <div style={{marginBottom: '30px'}}>
          <label htmlFor="message" style={{display: 'block', marginBottom: '8px', fontWeight: 500, color: 'var(--ozarks-blue-dark)'}}>
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={6}
            placeholder="Décrivez votre projet, vos besoins ou vos questions..."
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #E9ECEF',
              borderRadius: '8px',
              fontSize: '16px',
              transition: 'border-color 0.3s ease',
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--ozarks-blue-light)'}
            onBlur={(e) => e.target.style.borderColor = '#E9ECEF'}
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary"
          style={{
            width: '100%',
            padding: '15px',
            fontSize: '16px',
            fontWeight: 600,
            opacity: isSubmitting ? 0.7 : 1,
            cursor: isSubmitting ? 'not-allowed' : 'pointer'
          }}
        >
          {isSubmitting ? '📤 Envoi en cours...' : '📧 Envoyer le message'}
        </button>

        {submitStatus === 'success' && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            ✅ Gmail va s'ouvrir dans un nouvel onglet avec votre message pré-rempli
          </div>
        )}

        {submitStatus === 'error' && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            ❌ Une erreur s'est produite. Veuillez réessayer ou nous contacter directement.
          </div>
        )}
      </form>

      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: 'var(--ozarks-light-gray)',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <p style={{margin: '0', fontSize: '14px', color: 'var(--ozarks-gray)'}}>
          💡 <strong>Réponse rapide garantie</strong> - Nous vous répondons sous 24h maximum
        </p>
      </div>
    </div>
  );
};

export default ContactForm;